import React from "react";
import { Check, Plus } from "lucide-react";
import { SERVICE_CATALOGUE } from "@/lib/serviceCatalogue";

// ──────────────────────────────────────────────────────────────────────
// Shared "Extras" rendering primitives.
//
// Single source of truth used by BOTH the /booking page wizard AND the
// in-widget extras step on every other surface (home booking widget,
// hotel detail pages, sticky summary follow-ups). The user-facing card
// behaviour, the editable detail fields (date / time / people /
// duration / pickup / requirements / notes), the visual treatment and
// the data model are now identical across the site. Selection state is
// driven by `BookingContext.selectedServices` regardless of which
// surface the user touches.
//
// Both surfaces pass:
//   selectedServices: { [serviceKey]: detail | undefined }
//   toggleService(key)
//   updateServiceDetail(key, field, value)
// from `useBooking()` (the global booking provider).
// ──────────────────────────────────────────────────────────────────────

const pick = (obj, lang) => (obj && (obj[lang] || obj.en)) || "";

// Service keys that benefit from a "pickup / drop-off" field. Kept
// identical to the previous BookingPage definition so behaviour is
// unchanged on /booking and now applied uniformly in the widget too.
const SUPPORTS_PICKUP = new Set(["transfer", "camel-ride", "quad", "trekking"]);

export const ServiceDetailPanel = ({ skey, detail, onChange, lang }) => {
  const labels = {
    date: { en: "Preferred date", fr: "Date souhaitée", es: "Fecha preferida" },
    time: { en: "Preferred time", fr: "Heure souhaitée", es: "Hora preferida" },
    people: { en: "Number of people", fr: "Nombre de personnes", es: "Personas" },
    duration: { en: "Duration (if relevant)", fr: "Durée (si applicable)", es: "Duración (si aplica)" },
    pickup: { en: "Pickup / drop-off", fr: "Prise en charge / dépose", es: "Recogida / regreso" },
    requirements: { en: "Special requirements", fr: "Exigences spéciales", es: "Requisitos especiales" },
    notes: { en: "Notes for this service", fr: "Notes pour ce service", es: "Notas para este servicio" },
  };
  const supportsPickup = SUPPORTS_PICKUP.has(skey);
  const stop = (e) => e.stopPropagation();

  // Dark surface inside the card to match the selected (dark) card
  // background used on both /booking and the widget.
  const fieldCls =
    "w-full bg-[#0E0B0A] border border-[#FDFBF7]/15 text-[#FDFBF7] placeholder:text-[#FDFBF7]/35 px-3 py-2 text-sm focus:outline-none focus:border-[#D4A373] transition-colors";
  const labelCls = "text-[10px] tracking-[0.25em] uppercase text-[#FDFBF7]/70 mb-1";

  return (
    <div
      className="mt-4 pt-4 border-t border-[#D4A373]/30 grid grid-cols-2 gap-3"
      data-testid={`service-detail-${skey}`}
      onClick={stop}
    >
      <label className="flex flex-col" onClick={stop}>
        <span className={labelCls}>{pick(labels.date, lang)}</span>
        <input
          type="date"
          value={detail.date || ""}
          onChange={(e) => { stop(e); onChange("date", e.target.value); }}
          className={fieldCls}
          data-testid={`service-${skey}-date`}
        />
      </label>
      <label className="flex flex-col" onClick={stop}>
        <span className={labelCls}>{pick(labels.time, lang)}</span>
        <input
          type="time"
          value={detail.time || ""}
          onChange={(e) => { stop(e); onChange("time", e.target.value); }}
          className={fieldCls}
          data-testid={`service-${skey}-time`}
        />
      </label>
      <label className="flex flex-col" onClick={stop}>
        <span className={labelCls}>{pick(labels.people, lang)}</span>
        <input
          type="number"
          min="1"
          value={detail.people || ""}
          onChange={(e) => { stop(e); onChange("people", e.target.value); }}
          className={fieldCls}
          data-testid={`service-${skey}-people`}
        />
      </label>
      <label className="flex flex-col" onClick={stop}>
        <span className={labelCls}>{pick(labels.duration, lang)}</span>
        <input
          value={detail.duration || ""}
          onChange={(e) => { stop(e); onChange("duration", e.target.value); }}
          placeholder={pick({ en: "e.g. 60 min", fr: "ex. 60 min", es: "p.ej. 60 min" }, lang)}
          className={fieldCls}
          data-testid={`service-${skey}-duration`}
        />
      </label>
      {supportsPickup && (
        <label className="flex flex-col col-span-2" onClick={stop}>
          <span className={labelCls}>{pick(labels.pickup, lang)}</span>
          <input
            value={detail.pickup || ""}
            onChange={(e) => { stop(e); onChange("pickup", e.target.value); }}
            placeholder={pick({ en: "From / to (address or hotel)", fr: "Depuis / vers (adresse ou hôtel)", es: "Desde / hacia (dirección u hotel)" }, lang)}
            className={fieldCls}
            data-testid={`service-${skey}-pickup`}
          />
        </label>
      )}
      <label className="flex flex-col col-span-2" onClick={stop}>
        <span className={labelCls}>{pick(labels.requirements, lang)}</span>
        <input
          value={detail.requirements || ""}
          onChange={(e) => { stop(e); onChange("requirements", e.target.value); }}
          placeholder={pick({ en: "Vegetarian, allergies, accessibility…", fr: "Végétarien, allergies, accessibilité…", es: "Vegetariano, alergias, accesibilidad…" }, lang)}
          className={fieldCls}
          data-testid={`service-${skey}-requirements`}
        />
      </label>
      <label className="flex flex-col col-span-2" onClick={stop}>
        <span className={labelCls}>{pick(labels.notes, lang)}</span>
        <textarea
          rows={2}
          value={detail.notes || ""}
          onChange={(e) => { stop(e); onChange("notes", e.target.value); }}
          className={`${fieldCls} resize-none`}
          data-testid={`service-${skey}-notes`}
        />
      </label>
    </div>
  );
};

/**
 * One extras card. Identical visual treatment on /booking and the
 * embedded widget. Tap to add/remove; when selected the editable
 * `ServiceDetailPanel` opens inline with the same six fields.
 * `testIdPrefix` allows surfaces to namespace their data-testid
 * attributes ("booking" on the wizard, "widget" inside the floating
 * widget) without diverging in markup.
 */
export const ServiceCard = ({
  service,
  lang,
  selected,
  detail,
  onToggle,
  onChange,
  testIdPrefix = "service",
}) => {
  const Icon = service.Icon;
  return (
    <div
      className={`group text-left p-7 md:p-8 border transition-all duration-500 ${
        selected
          ? "bg-[#1A1513] border-[#D4A373] text-[#FDFBF7]"
          : "bg-[#FDFBF7] border-[#2C2621]/10 text-[#2C2621] hover:border-[#D4A373]/60"
      }`}
      data-testid={`${testIdPrefix}-service-card-${service.key}`}
    >
      <button
        type="button"
        onClick={() => onToggle(service.key)}
        className="w-full text-left"
        data-testid={`${testIdPrefix}-service-toggle-${service.key}`}
      >
        <div className="flex items-start justify-between gap-4">
          <span
            className={`inline-flex items-center justify-center w-11 h-11 border ${
              selected ? "border-[#D4A373]/60 text-[#D4A373]" : "border-[#D4A373]/45 text-[#A07042]"
            }`}
          >
            <Icon className="w-5 h-5" strokeWidth={1.5} />
          </span>
          <span
            className={`inline-flex items-center justify-center w-7 h-7 rounded-full transition-colors ${
              selected
                ? "bg-[#D4A373] text-[#1A1513]"
                : "border border-[#2C2621]/15 text-[#5C5248] group-hover:border-[#D4A373]"
            }`}
          >
            {selected ? <Check className="w-3.5 h-3.5" strokeWidth={2.2} /> : <Plus className="w-3.5 h-3.5" strokeWidth={1.8} />}
          </span>
        </div>
        <h3 className="mt-6 font-serif-x text-xl md:text-2xl tracking-tight leading-snug">
          {pick(service.title, lang)}
        </h3>
        <p className={`mt-2 text-sm leading-relaxed ${selected ? "text-[#FDFBF7]/75" : "text-[#5C5248]"}`}>
          {pick(service.desc, lang)}
        </p>
      </button>
      {selected && detail ? (
        <ServiceDetailPanel
          skey={service.key}
          detail={detail}
          onChange={(k, v) => onChange(service.key, k, v)}
          lang={lang}
        />
      ) : null}
    </div>
  );
};

/**
 * Renders the full extras grid for the active language using the
 * shared catalogue. Both /booking and the in-widget Step 2 mount this
 * exact component, so the visible cards, available fields and
 * selection state are guaranteed to match.
 *
 * Props:
 *   - selectedServices, toggleService, updateServiceDetail — read from
 *     `useBooking()` on the surface that mounts the grid.
 *   - testIdPrefix — "booking" on /booking, "widget" inside the
 *     HomeBookingWidget.
 *   - columns — Tailwind grid classes (defaults to 4 cols on lg, like
 *     the wizard). The widget can pass `lg:grid-cols-3` if it needs a
 *     slightly tighter layout, but the cards themselves remain
 *     identical.
 */
export const ExtrasGrid = ({
  lang,
  selectedServices,
  toggleService,
  updateServiceDetail,
  testIdPrefix = "booking",
  columns = "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4",
}) => (
  <div className={`grid ${columns} gap-4 md:gap-5`} data-testid={`${testIdPrefix}-extras-grid`}>
    {SERVICE_CATALOGUE.map((s) => (
      <ServiceCard
        key={`${testIdPrefix}-svc-${s.key}`}
        service={s}
        lang={lang}
        selected={Boolean(selectedServices[s.key])}
        detail={selectedServices[s.key]}
        onToggle={toggleService}
        onChange={updateServiceDetail}
        testIdPrefix={testIdPrefix}
      />
    ))}
  </div>
);

export default ExtrasGrid;
